import { Discount } from '@medusajs/medusa'
import type { ReductionRead } from '@miles/domain/Store/Models/Reduction/ReductionRead'
import Typography from '@mui/material/Typography'
import { ClientStatus, EditionPartnerRole } from '@prisma/client'
import { startOfDay } from 'date-fns'
import { Array, Option, pipe } from 'effect'
import { isNonEmptyArray } from 'effect/Array'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useState } from 'react'
import { SWRConfig, unstable_serialize } from 'swr'
import { match } from 'ts-pattern'
import { getRaceStatus } from 'components/Dashboard/Races/Status/getRaceStatus'
import { getShowRaceStock } from 'components/Dashboard/Races/Status/showRaceStock'
import { CurrencyExchangeRateContext } from 'components/Hook/CurrencyExchangeRateContext'
import Layout from 'components/Layout'
import BreadcrumbMicrodata from 'components/Microdata/Breadcrumb'
import EventMicrodata from 'components/Microdata/Event'
import VoteCard from 'components/VoteCard'
import { NewFlexContainer } from 'components/common/common.style'
import RaceCard from 'components/event/RaceCard/RaceCard'
import Banner from 'containers/event/Banner'
import {
  ContentFilterButton,
  EventContentGrid,
  EventCourse,
  EventRightContainer,
  EventRightContent,
  EventSectionContentFirst,
  FilterButton,
  MainContainer,
  PartnerScrollMarginSection,
  ScrollMarginSection
} from 'containers/event/Event.style'
import FixedMenu from 'containers/event/FixedMenu'
import { EventHead } from 'containers/event/Head'
import ImagesView from 'containers/event/ImagesView'
import IncludedRace from 'containers/event/IncludedRace'
import InfoCourse from 'containers/event/InfoCourse'
import Partner from 'containers/event/Partner'
import PracticalInformation from 'containers/event/PracticalInformation'
import Races from 'containers/event/Races'
import RegistrationLists from 'containers/event/RegistrationLists'
import { LocationContainer } from 'containers/event/map/LocationContainer'
import { MapContainer } from 'containers/event/map/MapContainer'
import { useGetGroupBySlug } from 'containers/event/useGetGroupBySlug'
import { raceSort } from 'containers/event/utils'
import type { LightGroup } from 'domain/models/Group/EntityGroup'
import type { Sponsor } from 'domain/models/Sponsor/Sponsor'
import type { VersionedProductId } from 'domain/models/ValueObject/EntityIds'
import { FrontReductionService } from 'domain/services/ReductionService/FrontReductionService'
import { getBaseUrl } from 'helpers'
import {
  isLevel1Category,
  isLevel2Category,
  raceCategoriesLevel1,
  urlParser
} from 'helpers/raceCategoryLevels'
import {
  liveApplyReductionForEditionService,
  liveAttendeeService,
  liveEventReadService,
  liveRateRepository,
  liveSponsorReadService,
  liveV1DiscountReadService
} from 'live/liveInstances'
import i18nConfig from 'next-i18next.config'
import type { OpenContestEdition } from 'services/api/database/ContestService'
import { ContestService } from 'services/api/database/ContestService'
import type { LiveEventById } from 'services/api/database/EventService'
import EventService from 'services/api/database/EventService'
import { useGetEditionAndEventFromEditionId } from 'services/editions/useGetEditionAndEventFromEditionId'
import { getBaseUrl as getEnvBaseUrl } from 'services/env'
import { EventPageParamsSchema } from 'services/validation/event'
import { AutoDiscountUtils } from 'store/services/AutoDiscountUtils'
import { defaultFrontLocales } from 'utils/locale'

export interface PreviousEditionAttendees {
  count: number
  editionYear: string
}

interface EventPageProps {
  currencyExchangeRates: Record<string, number>
  event: NonNullable<LiveEventById>
  impersonalReduction: ReductionRead[]
  previousEditionAttendeesCount: PreviousEditionAttendees | null
  autoDiscounts: Discount[]
  contest: OpenContestEdition | null
  slug: string
  attendeesFromEditionIdCount: number
  sponsors: Sponsor[]
  groupSlug: string | undefined
  fallback: { [key: string]: LightGroup | null }
}

const buildCtaRegisterLink = (
  group: LightGroup | null,
  slug: string | undefined | null,
  clientStatus: string | undefined | null,
  clientExternalUrl: string | undefined | null,
  locale: string | undefined
) =>
  match(clientStatus)
    .with(
      ClientStatus.NEW_SALES_FUNNEL,
      () =>
        `${locale ? `/${locale}` : ''}/checkout/${slug}/attendee${
          group ? `?groupSlug=${group.slug}` : ''
        }`
    )
    .otherwise(() => clientExternalUrl)

const Event = ({ fallback, ...props }: EventPageProps) => (
  <SWRConfig value={{ fallback }}>
    <EventPage {...props} />
  </SWRConfig>
)

const EventPage = ({
  event: {
    name,
    localizedContents: eventLocalizedContents,
    countrySubdivisionDisplayCodeLevel2,
    countrySubdivisionNameLevel2,
    facebookUrl,
    websiteUrl,
    instagramUrl,
    city,
    images,
    currentEdition: {
      localizedContents: editionLocalizedContents,
      calendarStatus,
      mainRace,
      races: racesWithoutReduction,
      clientStatus,
      clientExternalUrl,
      registrationOpeningDate,
      registrationClosingDate,
      editionInfo,
      startDate,
      currency,
      endDate,
      timeZone,
      id: editionId,
      editionPartners
    },
    longitude,
    latitude,
    id,
    isFeatured
  },
  currencyExchangeRates,
  contest,
  slug,
  attendeesFromEditionIdCount,
  impersonalReduction,
  sponsors,
  autoDiscounts,
  groupSlug,
  previousEditionAttendeesCount
}: Omit<EventPageProps, 'fallback'>) => {
  const { t } = useTranslation(['common', 'event'])
  const [activeMenuItem, setActiveMenuItem] = useState(t('event:apropos'))
  const { locale, defaultLocale } = useRouter()
  const group = useGetGroupBySlug({ slug: groupSlug }).data ?? null

  const applicableAutoDiscounts = autoDiscounts.filter(
    AutoDiscountUtils.unformattedDiscountIsActive(null)
  )

  const races =
    isNonEmptyArray(impersonalReduction) ||
    isNonEmptyArray(applicableAutoDiscounts)
      ? racesWithoutReduction.map((race) => {
          const autoDiscount = applicableAutoDiscounts.filter(
            ({ rule: { conditions } }) =>
              conditions.some(({ products }) =>
                products.some(({ id }) => id === race.medusaProductId)
              )
          )[0]

          const priceWithReductionFromDiscount = race.price
            ? race.price -
              (autoDiscount
                ? AutoDiscountUtils.getPriceInCurrency(currency)(autoDiscount)
                : 0)
            : null

          return {
            ...race,
            priceWithReduction: isNonEmptyArray(impersonalReduction)
              ? FrontReductionService.reducePriceDependingOnRace(
                  impersonalReduction,
                  {
                    ...race,
                    priceWithReduction: priceWithReductionFromDiscount
                  }
                )
              : priceWithReductionFromDiscount
          }
        })
      : racesWithoutReduction

  const price = mainRace?.price

  const mainType = mainRace?.categoryLevel1
  const mainDistance = mainRace?.categoryLevel2

  const typeLabel = mainType ? t(mainType as never) : ''
  const typeUrl = mainType
    ? raceCategoriesLevel1[mainType as keyof typeof raceCategoriesLevel1].url
    : ''
  const distanceLabel = mainDistance ? t(mainDistance as never) : ''
  const distanceUrl = urlParser(distanceLabel)
  const isValidBreadcrumb =
    mainType &&
    isLevel1Category(mainType) &&
    mainDistance &&
    isLevel2Category(mainDistance)

  const categoryTypes = pipe(
    races,
    Array.filterMap(({ categoryLevel1 }) =>
      Option.fromNullable(categoryLevel1)
    ),
    Array.dedupe
  )
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  const handleCategoryClick = (category: string | null) => {
    setSelectedCategory(category === selectedCategory ? null : category)
  }

  const filteredRaces =
    selectedCategory === null
      ? races
      : races.filter((race) => race.categoryLevel1 === selectedCategory)

  const { data: editionAndEvent } = useGetEditionAndEventFromEditionId({
    editionId
  })

  const hasInformation =
    !!editionInfo?.editionServices && editionInfo.editionServices.length > 0

  const hasParcours =
    races.filter(({ raceInfo }) => raceInfo && raceInfo.gpxFileUrl).length > 0

  const hasLocation = !hasParcours && !!latitude && !!longitude

  const practicalInformation =
    isNonEmptyArray(editionLocalizedContents) &&
    editionLocalizedContents[0].usefulInformation

  const editionOrganizers = editionPartners.filter(
    (partner) => partner.role === EditionPartnerRole.ORGANIZER
  )
  const organizersInformation =
    editionOrganizers.length > 0
      ? editionOrganizers
      : // Keep this for backward compatibility
      facebookUrl || websiteUrl || instagramUrl
      ? [
          {
            facebookUrl,
            websiteUrl,
            instagramUrl,
            name: null,
            logoUrl: null,
            role: EditionPartnerRole.ORGANIZER,
            localizedContents: []
          }
        ]
      : []

  const timersInformation = editionPartners.filter(
    (partner) => partner.role === EditionPartnerRole.TIMER
  )

  const peyceReview = isNonEmptyArray(eventLocalizedContents)
    ? eventLocalizedContents[0].review
    : ''

  const basePath = `${process.env.APP_URI}${
    locale === defaultLocale ? '' : `/${locale}`
  }`

  const today = startOfDay(new Date())
  const isOver = endDate
    ? startOfDay(endDate) < today
    : startDate
    ? startOfDay(startDate) <= today
    : false
  const showVoteCard =
    contest &&
    mainRace?.categoryLevel1 &&
    contest.raceTypes.includes(mainRace.categoryLevel1)
  return (
    <CurrencyExchangeRateContext.Provider value={currencyExchangeRates}>
      <Layout group={group} rounded={false}>
        <EventHead
          calendarStatus={calendarStatus}
          city={city}
          startDate={startDate}
          endDate={endDate}
          timeZone={timeZone}
          currency={currency}
          name={name}
          image={images && images.length > 0 ? images[0] : null}
        />
        {isValidBreadcrumb ? (
          <BreadcrumbMicrodata
            basePath={basePath}
            data={[
              {
                link: '/les-courses',
                label: t('eventPage.breadcrumb.allRaces')
              },
              { link: `/${typeUrl}`, label: typeLabel },
              { link: `/${distanceUrl}`, label: distanceLabel },
              { link: `/event/${slug}`, label: name }
            ]}
          />
        ) : null}
        <EventMicrodata
          basePath={basePath}
          name={name}
          startDate={startDate}
          endDate={endDate}
          currency={currency}
          longitude={longitude}
          latitude={latitude}
          slug={slug}
          city={city}
          images={images}
          price={price}
          organizer={''} // does not seem to exist
          organizerUrl={websiteUrl || facebookUrl}
          peyceReview={peyceReview}
          races={races}
        />
        <Banner
          mainRace={mainRace}
          slug={slug}
          name={name}
          eventId={id}
          city={city}
          countrySubdivisionDisplayCodeLevel2={
            countrySubdivisionDisplayCodeLevel2
          }
          contest={contest}
          editionCalendarStatus={calendarStatus}
          isOver={isOver}
        />
        {images.length > 0 && (
          <ImagesView images={images} eventId={id} eventName={name} />
        )}
        <MainContainer>
          <EventSectionContentFirst $hasGroup={!!group}>
            <FixedMenu
              activeItem={activeMenuItem}
              onItemClick={setActiveMenuItem}
              hasInformation={hasInformation}
              hasParcours={hasParcours}
              hasLocation={hasLocation}
              hasPracticalInformation={!!practicalInformation}
              hasRegistrationLists={attendeesFromEditionIdCount > 0}
              hasOrganizerInformation={organizersInformation.length > 0}
              hasTimerInformation={timersInformation.length > 0}
            />
          </EventSectionContentFirst>
          <EventContentGrid>
            <NewFlexContainer flexDirection="column" gap="20px">
              <ScrollMarginSection id={t('event:aboutAnchor')}>
                <InfoCourse
                  previousEditionAttendeesCount={previousEditionAttendeesCount}
                  calendarStatus={calendarStatus}
                  startDate={startDate}
                  endDate={endDate}
                  timeZone={timeZone}
                  city={city}
                  countrySubdivisionDisplayCodeLevel2={
                    countrySubdivisionDisplayCodeLevel2
                  }
                  countrySubdivisionNameLevel2={countrySubdivisionNameLevel2}
                  contest={contest}
                  peyceReview={peyceReview}
                  editionStart={registrationOpeningDate}
                  editionEnd={registrationClosingDate}
                  isOver={isOver}
                  categoryLevel1={mainRace?.categoryLevel1}
                />
              </ScrollMarginSection>
              <ScrollMarginSection id={t('event:raceAnchor')}>
                <EventCourse>
                  <Typography variant="h4">{t('run')}</Typography>
                  <ContentFilterButton>
                    {['all', ...categoryTypes].map((category) => (
                      <FilterButton
                        key={category}
                        $category={category}
                        $selectedCategory={selectedCategory}
                        onClick={() =>
                          handleCategoryClick(
                            category === 'all' ? null : category
                          )
                        }
                      >
                        {t(category as never)}
                      </FilterButton>
                    ))}
                  </ContentFilterButton>
                  <Races
                    filteredRaces={filteredRaces}
                    buildCtaRegisterLink={(slug) =>
                      buildCtaRegisterLink(
                        group,
                        slug,
                        clientStatus,
                        clientExternalUrl,
                        defaultLocale === locale ? undefined : locale
                      )
                    }
                    calendarStatus={calendarStatus}
                    websiteUrl={websiteUrl}
                    facebookUrl={facebookUrl}
                    isFeatured={isFeatured}
                    sponsors={sponsors}
                    registrationOpeningDate={registrationOpeningDate}
                    registrationClosingDate={registrationClosingDate}
                    group={group}
                  />
                </EventCourse>
              </ScrollMarginSection>
              {attendeesFromEditionIdCount > 0 && (
                <ScrollMarginSection id={t('event:registrationListAnchor')}>
                  <RegistrationLists
                    registredNumbers={attendeesFromEditionIdCount}
                    link={`${getEnvBaseUrl()}/registrants/${
                      editionAndEvent?.data.slug
                    }`}
                  />
                </ScrollMarginSection>
              )}
              {hasParcours && (
                <ScrollMarginSection id={t('event:parcoursAnchor')}>
                  <MapContainer races={races} />
                </ScrollMarginSection>
              )}
              {hasLocation && (
                <ScrollMarginSection id={t('event:locationAnchor')}>
                  <LocationContainer
                    latitude={latitude}
                    longitude={longitude}
                  />
                </ScrollMarginSection>
              )}
              {hasInformation && (
                <ScrollMarginSection id={t('event:informationAnchor')}>
                  <IncludedRace
                    title={t('event:whatIsIncluded')}
                    editionServices={editionInfo?.editionServices}
                  />
                </ScrollMarginSection>
              )}
              {practicalInformation && (
                <ScrollMarginSection id={t('event:practicalInformationAnchor')}>
                  <PracticalInformation content={practicalInformation} />
                </ScrollMarginSection>
              )}
              {organizersInformation.length > 0 && (
                <PartnerScrollMarginSection id={t('event:organizerAnchor')}>
                  {organizersInformation.map((organizerInformation) => (
                    <Partner
                      key={organizerInformation.name ?? 'organizer'}
                      information={organizerInformation}
                    />
                  ))}
                </PartnerScrollMarginSection>
              )}
              {timersInformation.length > 0 && (
                <PartnerScrollMarginSection id={t('event:timerAnchor')}>
                  {timersInformation.map((timerInformation) => (
                    <Partner
                      key={timerInformation.name ?? 'timer'}
                      information={timerInformation}
                    />
                  ))}
                </PartnerScrollMarginSection>
              )}
            </NewFlexContainer>
            <EventRightContainer $hasGroup={!!group}>
              {showVoteCard && (
                <EventRightContent
                  style={{
                    paddingBottom: 0,
                    paddingTop: '10px',
                    minHeight: 'fit-content'
                  }}
                >
                  <VoteCard
                    event={{
                      id,
                      city,
                      countrySubdivisionDisplayCodeLevel2,
                      name
                    }}
                    badge={mainRace.categoryLevel1}
                    contest={contest}
                  />
                </EventRightContent>
              )}
              <EventRightContent>
                <Typography variant="h4">{t('chooseArace')}</Typography>
                {races.sort(raceSort).map((race, index) => {
                  const raceForStatuses = {
                    ...race,
                    editionRegistrationOpeningDate: registrationOpeningDate
                      ? registrationOpeningDate
                      : null,
                    editionRegistrationClosingDate: registrationClosingDate
                      ? registrationClosingDate
                      : null,
                    registrationsCount: race.attendeeCounts,
                    raceStockInventory: race.raceInventory,
                    group,
                    withProjected: true,
                    raceSegmentsLength: race.raceSegments.length,
                    stockDisplayThreshold: race.stockDisplayThreshold,
                    stockDisplayThresholdValue: race.stockDisplayThresholdValue,
                    isWaitingList:
                      race.raceWaitingListConfiguration?.enabled ?? false
                  }
                  const raceStatus = getRaceStatus(raceForStatuses)
                  const showRaceStock = getShowRaceStock(raceForStatuses)

                  const registerCtaLink = buildCtaRegisterLink(
                    group,
                    race.slug,
                    clientStatus,
                    clientExternalUrl,
                    defaultLocale === locale ? undefined : locale
                  )
                  const defaultLink = websiteUrl ? websiteUrl : facebookUrl
                  const raceCtaLink = isFeatured ? registerCtaLink : defaultLink

                  return (
                    <RaceCard
                      key={`event_card-${race.slug ?? index}`}
                      race={race}
                      ctaLink={raceCtaLink ?? '/'}
                      sponsors={sponsors}
                      raceStatus={raceStatus}
                      cardType="RESUME"
                      isFeatured={isFeatured}
                      showRaceStock={showRaceStock}
                      groupRegistrationOpeningDate={
                        group?.registrationOpeningDate
                      }
                    />
                  )
                })}
              </EventRightContent>
            </EventRightContainer>
          </EventContentGrid>
        </MainContainer>
      </Layout>
    </CurrencyExchangeRateContext.Provider>
  )
}

const eventService = new EventService()
const contestService = new ContestService()

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps<EventPageProps> = async ({
  locale,
  params
}: {
  locale?: string
  params?: {
    slugs?: string[]
  }
}) => {
  if (!locale) {
    return {
      notFound: true
    }
  }

  const paramsParseResult = EventPageParamsSchema.safeParse(params)
  if (paramsParseResult.error) {
    return {
      notFound: true
    }
  }
  const [urlSlug, groupSlug] = paramsParseResult.data.slugs
  const idString = urlSlug.split('-').pop()
  if (!idString) {
    return {
      notFound: true
    }
  }
  const id = parseInt(idString)

  if (Number.isNaN(id)) {
    return {
      notFound: true
    }
  }

  const { redirect, slug } = await eventService.getNewSlugById({ id })
  if (redirect) {
    return {
      redirect: {
        permanent: true,
        destination: `${getBaseUrl(locale)}/event/${slug}`
      }
    }
  } else {
    try {
      const { event, group } =
        await liveEventReadService.getEventPageDataByIdAndGroupSlug(
          id,
          locale,
          groupSlug
        )
      const productIds =
        event?.currentEdition?.races?.flatMap((race): VersionedProductId[] =>
          race.medusaProductId
            ? [
                {
                  productId: race.medusaProductId,
                  version: event.currentEdition.medusaVersion
                }
              ]
            : []
        ) ?? []
      if (!event || !event.currentEdition) {
        return { notFound: true }
      }

      if (urlSlug !== event.slug) {
        return {
          redirect: {
            destination: `${getBaseUrl(locale)}/event/${event.slug}`,
            permanent: true
          }
        }
      }

      const attendeesFromEditionIdCount = event.currentEdition
        .isAttendeeListPublic
        ? await liveAttendeeService.getAttendeePublicListingCount(
            event.currentEdition.id
          )
        : 0

      const sponsors = await liveSponsorReadService.getEditionSponsors(
        event.currentEdition.id
      )
      const contest = await contestService.getOpenContestEdition()

      const currencyExchangeRates =
        await liveRateRepository.getRunnerTargetRates(
          event.currentEdition.currency
        )

      const [impersonalReduction, autoDiscounts] = await Promise.all([
        liveApplyReductionForEditionService.retrieveImpersonalReductions(
          event.currentEdition.races,
          group
        ),
        liveV1DiscountReadService.getUnformattedDiscountsByProductIds(
          pipe(
            productIds,
            Array.filterMap(({ version, productId }) =>
              version === 'V1' ? Option.some(productId) : Option.none()
            )
          ),
          'AUTO_DISCOUNT'
        )
      ])

      const editionYear = Number(event.currentEdition.year)
      const lastEditionYear = Number.isNaN(editionYear)
        ? null
        : String(editionYear - 1)

      const previousEditionAttendeesCount = lastEditionYear
        ? await eventService.getEditionRegistrantsNumberByYear(
            event.id,
            lastEditionYear
          )
        : null

      return {
        props: {
          fallback: {
            [unstable_serialize(`/groups/${group?.slug}`)]: group
          },
          impersonalReduction,
          autoDiscounts,
          previousEditionAttendeesCount:
            previousEditionAttendeesCount && lastEditionYear
              ? {
                  count: previousEditionAttendeesCount,
                  editionYear: lastEditionYear
                }
              : null,
          event,
          currencyExchangeRates,
          slug: urlSlug,
          groupSlug,
          contest,
          attendeesFromEditionIdCount,
          sponsors,
          ...(await serverSideTranslations(
            locale,
            [
              ...defaultFrontLocales,
              'checkout',
              'zod',
              'event',
              'whatsincluded',
              'vote'
            ],
            i18nConfig
          ))
        }
      }
    } catch (err) {
      console.error(err)
      return { notFound: true }
    }
  }
}

export default Event
